// src/features/customers/components/CustomerTable.tsx
import { useEffect, useMemo, useState } from 'react';
import { Grid } from 'gridjs-react';
import { h } from 'gridjs';
import 'gridjs/dist/theme/mermaid.css';
import type { CustomerRecord, CustomerFilters } from '../types';

interface CustomerTableProps {
  data: CustomerRecord[];
  isLoading: boolean;
  filters: CustomerFilters;
  onFiltersChange: (filters: CustomerFilters) => void;
  onEdit: (record: CustomerRecord) => void;
  onToggleStatus: (record: CustomerRecord) => void;
  onDelete: (record: CustomerRecord) => void;
  onResetPassword: (record: CustomerRecord) => void;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export function CustomerTable({
  data,
  isLoading: _isLoading,
  filters,
  onFiltersChange,
  onEdit,
  onToggleStatus,
  onDelete,
  onResetPassword,
  selectedIds,
  onSelectionChange,
}: CustomerTableProps) {
  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  const [statusValue, setStatusValue] = useState<CustomerFilters['status']>(filters.status ?? 'all');

  useEffect(() => {
    setSearchInput(filters.search ?? '');
  }, [filters.search]);

  useEffect(() => {
    setStatusValue(filters.status ?? 'all');
  }, [filters.status]);

  // Tối ưu: recordMap dùng useMemo
  const recordMap = useMemo(() => new Map(data.map((record) => [record.id, record])), [data]);

  // Tối ưu: gridData dùng useMemo
  const gridData = useMemo(
    () =>
      data.map((record, index) => ({
        checkbox: record.id,
        stt: index + 1,
        name: record.name,
        representative_name: record.representative_name ?? '–',
        email: record.email ?? '–',
        phone: record.phone ?? record.representative_phone ?? '–',
        status: record.status,
        subscriptions_count: record.subscriptions?.length ?? 0,
        actions: record.id,
        _record: record,
      })),
    [data]
  );

  // Tạo checkbox header element với indeterminate state
  const checkboxHeaderElement = useMemo(() => {
    const allSelected = data.length > 0 && selectedIds.length === data.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < data.length;
    const checkboxId = 'customer-select-all-checkbox';

    // Set indeterminate sau khi render
    setTimeout(() => {
      const checkbox = document.getElementById(checkboxId) as HTMLInputElement;
      if (checkbox) {
        checkbox.indeterminate = someSelected;
      }
    }, 0);

    return h('div', { style: { display: 'flex', justifyContent: 'center' } }, [
      h('input', {
        id: checkboxId,
        type: 'checkbox',
        checked: allSelected,
        onChange: (e: Event) => {
          const input = e.target as HTMLInputElement;
          if (input.checked) {
            onSelectionChange(data.map((item) => item.id));
          } else {
            onSelectionChange([]);
          }
        },
      }),
    ]);
  }, [data, selectedIds, onSelectionChange]);

  // Tối ưu: columns dùng useMemo với formatters thuần
  const columns = useMemo(() => {
    // Pure formatters - không dùng useCallback, không dùng factory pattern
    const formatCheckbox = (cell: unknown) => {
      const recordId = cell as string;
      const record = recordMap.get(recordId);
      if (!record) return null;
      return h('div', { style: { display: 'flex', justifyContent: 'center' } }, [
        h('input', {
          type: 'checkbox',
          checked: selectedIds.includes(record.id),
          onChange: (e: Event) => {
            const input = e.target as HTMLInputElement;
            if (input.checked) {
              onSelectionChange([...new Set([...selectedIds, record.id])]);
            } else {
              onSelectionChange(selectedIds.filter((id) => id !== record.id));
            }
          },
        }),
      ]);
    };

    const formatSTT = (cell: unknown) => {
      return h('span', {}, String(cell || ''));
    };

    const formatName = (cell: unknown) => {
      const text = typeof cell === 'string' ? cell : '–';
      return h(
        'div',
        {
          className: 'truncate-cell',
          title: text,
          style: { maxWidth: '220px' },
        },
        text
      );
    };

    const formatRepresentativeName = (cell: unknown) => {
      const text = typeof cell === 'string' ? cell : '–';
      return h(
        'div',
        {
          className: 'truncate-cell',
          title: text,
          style: { maxWidth: '180px' },
        },
        text
      );
    };

    const formatEmail = (cell: unknown) => {
      const text = typeof cell === 'string' ? cell : '–';
      return h(
        'div',
        {
          className: 'truncate-cell',
          title: text,
          style: { maxWidth: '240px' },
        },
        text
      );
    };

    const formatPhone = (cell: unknown) => {
      const text = typeof cell === 'string' ? cell : '–';
      return h(
        'div',
        {
          className: 'truncate-cell',
          title: text,
          style: { maxWidth: '140px' },
        },
        text
      );
    };

    const formatSubscriptions = (cell: unknown) => {
      const count = typeof cell === 'number' ? cell : 0;
      return h('span', { className: 'badge bg-info' }, `${count} gói`);
    };

    const formatStatus = (cell: unknown) => {
      const isActive = (cell as string) === 'active';
      return h(
        'span',
        {
          className: isActive ? 'badge bg-success' : 'badge bg-secondary',
          style: { minWidth: 72, display: 'inline-block', textAlign: 'center' },
        },
        isActive ? 'Hoạt động' : 'Vô hiệu hóa'
      );
    };

    const formatActions = (cell: unknown) => {
      const id = cell as string;
      const record = recordMap.get(id);
      if (!record) return null;
      return h('div', { className: 'table-actions' }, [
        h(
          'button',
          {
            className: 'btn btn-sm',
            title: 'Chỉnh sửa',
            onClick: () => onEdit(record),
            style: { width: 32, height: 32, padding: 0 },
          },
          h('i', { className: 'ti ti-edit' })
        ),
        h(
          'button',
          {
            className: 'btn btn-sm',
            title: record.status === 'active' ? 'Vô hiệu hóa' : 'Kích hoạt',
            onClick: () => onToggleStatus(record),
            style: { width: 32, height: 32, padding: 0 },
          },
          h('i', { className: record.status === 'active' ? 'ti ti-check' : 'ti ti-x' })
        ),
        h(
          'button',
          {
            className: 'btn btn-sm',
            title: 'Reset mật khẩu',
            onClick: () => onResetPassword(record),
            style: { width: 32, height: 32, padding: 0 },
          },
          h('i', { className: 'ti ti-key' })
        ),
        h(
          'button',
          {
            className: 'btn btn-sm btn-danger',
            title: 'Xóa',
            onClick: () => onDelete(record),
            style: { width: 32, height: 32, padding: 0 },
          },
          h('i', { className: 'ti ti-trash' })
        ),
        // Dropdown cho màn hình nhỏ
        h(
          'div',
          { className: 'actions-dropdown', style: { display: 'none' } },
          h(
            'button',
            { className: 'btn btn-sm', title: 'Thao tác' },
            h('i', { className: 'ti ti-dots-vertical' })
          )
        ),
      ]);
    };

    return [
      {
        id: 'checkbox',
        name: checkboxHeaderElement,
        formatter: formatCheckbox,
        sort: false, // Tắt sort cho checkbox
        width: '60px',
      },
      {
        id: 'stt',
        name: '#',
        formatter: formatSTT,
        sort: false, // Tắt sort cho STT
        width: '60px',
      },
      {
        id: 'name',
        name: 'Tên công ty',
        formatter: formatName,
        width: '220px',
      },
      {
        id: 'representative_name',
        name: 'Người đại diện',
        formatter: formatRepresentativeName,
        width: '180px',
      },
      {
        id: 'email',
        name: 'Email',
        formatter: formatEmail,
        sort: false, // Tắt sort cho email
        width: '240px',
      },
      {
        id: 'phone',
        name: 'SĐT',
        formatter: formatPhone,
        sort: false, // Tắt sort cho SĐT
        width: '140px',
      },
      {
        id: 'subscriptions_count',
        name: 'Gói',
        formatter: formatSubscriptions,
        sort: false, // Tắt sort cho gói đăng ký
        width: '120px',
      },
      {
        id: 'status',
        name: 'Trạng thái',
        formatter: formatStatus,
        sort: false, // Tắt sort cho trạng thái
        width: '140px',
      },
      {
        id: 'actions',
        name: 'Hành động',
        formatter: formatActions,
        sort: false,
        width: '160px',
      },
    ];
  }, [recordMap, selectedIds, onSelectionChange, onEdit, onToggleStatus, onResetPassword, onDelete, checkboxHeaderElement]);

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div
          className="toolbar-actions"
          style={{ gap: '12px', alignItems: 'center', flexWrap: 'wrap', display: 'flex' }}
        >
          <select
            className="status-filter__select"
            value={statusValue}
            onChange={(event) => {
              const nextStatus = event.target.value as CustomerFilters['status'];
              setStatusValue(nextStatus);
              onFiltersChange({ ...filters, status: nextStatus, search: searchInput });
            }}
            aria-label="Lọc trạng thái khách hàng"
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="active">Đang hoạt động</option>
            <option value="inactive">Vô hiệu hoá</option>
          </select>
          <input
            type="search"
            className="form-control form-control-sm toolbar-input"
            style={{ width: 240 }}
            placeholder="Tìm kiếm tên, email, MST..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              onFiltersChange({ ...filters, search: e.target.value, status: statusValue });
            }}
          />
        </div>
      </div>

      <div className="vuexy-table-wrapper">
        <Grid
          data={gridData}
          columns={columns}
          search={false}
          sort={true}
          pagination={{ limit: 10, summary: true }}
          className={{ table: 'vuexy-table' }}
          language={{
            pagination: {
              previous: '‹',
              next: '›',
              showing: 'Hiển thị',
              results: () => 'kết quả',
            },
          }}
        />
      </div>
    </div>
  );
}
